import axios, { AxiosRequestConfig } from 'axios'
import { STORAGE_KEYS } from './storageKeys'
import {
  clearAuthStorageByPrefix,
  getAuthStorageItem,
  removeAuthStorageItem,
  setAuthStorageItem,
} from './authStorage'

type QueueItem = {
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}

function refreshFoiRecusado(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return true
  const status = error.response?.status
  return status === 401 || status === 400
}

export class TokenRefreshManager {
  private isRefreshing = false
  private failedQueue: QueueItem[] = []

  private processQueue(error: unknown, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error)
      } else {
        resolve(token)
      }
    })
    this.failedQueue = []
  }

  async handle401(
    originalRequest: AxiosRequestConfig & { _retry?: boolean },
    apiBaseUrl: string
  ) {
    // Already in progress: queue and wait
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject })
      })
        .then(token => {
          // Inject new token into the queued request
          originalRequest.headers = originalRequest.headers || {}
          ;(originalRequest.headers as Record<string, string>).Authorization =
            `Bearer ${token}`
          return axios(originalRequest)
        })
        .catch(err => Promise.reject(err))
    }

    originalRequest._retry = true
    this.isRefreshing = true

    const refreshToken = getAuthStorageItem(STORAGE_KEYS.REFRESH_TOKEN)

    if (!refreshToken) {
      this.clearTokensAndRedirect()
      this.isRefreshing = false
      return Promise.reject(new Error('No refresh token available'))
    }

    try {
      const response = await axios.post(`${apiBaseUrl}/api/auth/refresh/`, {
        refresh: refreshToken,
      })

      const { access, features } = response.data
      setAuthStorageItem(STORAGE_KEYS.ACCESS_TOKEN, access)

      // Update features if returned from backend
      if (features) {
        setAuthStorageItem(STORAGE_KEYS.FEATURES, JSON.stringify(features))
        // Dispatch event so AuthProvider can update state
        window.dispatchEvent(
          new CustomEvent('featuresUpdated', { detail: features })
        )
      }

      this.processQueue(null, access)

      // Retry original request with new token
      originalRequest.headers = originalRequest.headers || {}
      ;(originalRequest.headers as Record<string, string>).Authorization =
        `Bearer ${access}`
      return axios(originalRequest)
    } catch (refreshError) {
      this.processQueue(refreshError, null)
      // Só a recusa do refresh (401/400) quer dizer sessão vencida. Servidor
      // desligado ou reiniciando também cai aqui, e deslogar nesse caso jogava
      // a pessoa na tela de login com o servidor fora e o trabalho perdido.
      if (refreshFoiRecusado(refreshError)) this.clearTokensAndRedirect()
      return Promise.reject(refreshError)
    } finally {
      this.isRefreshing = false
    }
  }

  private clearTokensAndRedirect() {
    removeAuthStorageItem(STORAGE_KEYS.ACCESS_TOKEN)
    removeAuthStorageItem(STORAGE_KEYS.REFRESH_TOKEN)
    removeAuthStorageItem(STORAGE_KEYS.USER)
    removeAuthStorageItem(STORAGE_KEYS.PROFILE)
    removeAuthStorageItem(STORAGE_KEYS.PERMISSIONS)
    removeAuthStorageItem(STORAGE_KEYS.FEATURES)
    removeAuthStorageItem(STORAGE_KEYS.AGENTS_CACHE)
    removeAuthStorageItem(STORAGE_KEYS.PROFILE_CACHE)
    clearAuthStorageByPrefix('optimus_')
    window.location.href = '/signin'
  }
}

export const tokenRefreshManager = new TokenRefreshManager()
